import React from 'react';
import { Copy, FileSpreadsheet, FileText, Printer } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { useToast } from '../../hooks/use-toast';

const ExportButtons = ({ data, columns, filename = 'export' }) => {
  const { toast } = useToast();

  // Copy to clipboard
  const handleCopy = () => {
    if (!data || data.length === 0) {
      toast({
        title: 'No Data',
        description: 'No data available to copy',
        variant: 'destructive'
      });
      return;
    }

    // Create text from data
    const headers = columns.map(col => col.header).join('\t');
    const rows = data.map(row => 
      columns.map(col => {
        const value = col.accessor ? col.accessor(row) : row[col.key];
        return typeof value === 'string' ? value : JSON.stringify(value);
      }).join('\t')
    ).join('\n');
    
    const text = headers + '\n' + rows;
    
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: 'Success',
        description: `Copied ${data.length} rows to clipboard`,
      });
    }).catch(err => {
      console.error('Failed to copy:', err);
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive'
      });
    });
  };

  // Export to Excel
  const handleExcel = () => {
    try {
      if (!data || data.length === 0) {
        toast({
          title: 'No Data',
          description: 'No data available to export',
          variant: 'destructive'
        });
        return;
      }

      // Prepare data for Excel
      const excelData = data.map(row => {
        const rowData = {};
        columns.forEach(col => {
          const value = col.accessor ? col.accessor(row) : row[col.key];
          // Handle null/undefined values
          if (value === null || value === undefined) {
            rowData[col.header] = 'N/A';
          } else if (typeof value === 'string') {
            rowData[col.header] = value;
          } else if (typeof value === 'object') {
            rowData[col.header] = JSON.stringify(value);
          } else {
            rowData[col.header] = String(value);
          }
        });
        return rowData;
      });

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      
      // Download
      XLSX.writeFile(wb, `${filename}.xlsx`);
      toast({
        title: 'Success',
        description: `Excel file downloaded (${data.length} rows)`,
      });
    } catch (error) {
      console.error('Excel Export Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to export Excel: ' + error.message,
        variant: 'destructive'
      });
    }
  };

  // Export to PDF
  const handlePDF = () => {
    try {
      if (!data || data.length === 0) {
        toast({
          title: 'No Data',
          description: 'No data available to export',
          variant: 'destructive'
        });
        return;
      }

      const doc = new jsPDF();
      
      // Prepare table data
      const headers = [columns.map(col => col.header)];
      const rows = data.map(row => 
        columns.map(col => {
          const value = col.accessor ? col.accessor(row) : row[col.key];
          // Handle null/undefined values
          if (value === null || value === undefined) return 'N/A';
          return typeof value === 'string' ? value : String(value);
        })
      );

      // Add title
      doc.setFontSize(16);
      doc.text(filename.replace(/-/g, ' ').toUpperCase(), 14, 15);

      // Add table to PDF using autoTable plugin
      autoTable(doc, {
        head: headers,
        body: rows,
        startY: 25,
        styles: { 
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: { 
          fillColor: [147, 51, 234],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        }
      });

      // Download
      doc.save(`${filename}.pdf`);
      toast({
        title: 'Success',
        description: `PDF file downloaded (${data.length} rows)`,
      });
    } catch (error) {
      console.error('PDF Export Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to export PDF: ' + error.message,
        variant: 'destructive'
      });
    }
  };

  // Print
  const handlePrint = () => {
    try {
      if (!data || data.length === 0) {
        toast({
          title: 'No Data',
          description: 'No data available to print',
          variant: 'destructive'
        });
        return;
      }

      // Create printable HTML
      const headers = columns.map(col => `<th style="border: 1px solid #ddd; padding: 8px; background-color: #9333ea; color: white;">${col.header}</th>`).join('');
      const rows = data.map(row => {
        const cells = columns.map(col => {
          const value = col.accessor ? col.accessor(row) : row[col.key];
          let displayValue = 'N/A';
          if (value !== null && value !== undefined) {
            displayValue = typeof value === 'string' ? value : String(value);
          }
          return `<td style="border: 1px solid #ddd; padding: 8px;">${displayValue}</td>`;
        }).join('');
        return `<tr>${cells}</tr>`;
      }).join('');

      const printWindow = window.open('', '', 'height=600,width=800');
      printWindow.document.write(`
        <html>
          <head>
            <title>Print - ${filename}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h2 { color: #9333ea; margin-bottom: 20px; }
              table { border-collapse: collapse; width: 100%; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
              th { background-color: #9333ea; color: white; font-weight: bold; }
              tr:nth-child(even) { background-color: #f9f9f9; }
              @media print {
                body { margin: 0; }
              }
            </style>
          </head>
          <body>
            <h2>${filename.replace(/-/g, ' ').toUpperCase()}</h2>
            <table>
              <thead><tr>${headers}</tr></thead>
              <tbody>${rows}</tbody>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
      
      toast({
        title: 'Print',
        description: 'Print dialog opened',
      });
    } catch (error) {
      console.error('Print Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to print: ' + error.message,
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="flex gap-2 mb-4">
      <button
        onClick={handleCopy}
        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 transition-colors"
        title="Copy to clipboard"
      >
        <Copy className="w-4 h-4" />
        Copy
      </button>
      
      <button
        onClick={handleExcel}
        className="px-3 py-2 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-lg flex items-center gap-2 text-sm text-green-700 dark:text-green-300 transition-colors"
        title="Export to Excel"
      >
        <FileSpreadsheet className="w-4 h-4" />
        Excel
      </button>
      
      <button
        onClick={handlePDF}
        className="px-3 py-2 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-300 transition-colors"
        title="Export to PDF"
      >
        <FileText className="w-4 h-4" />
        PDF
      </button>
      
      <button
        onClick={handlePrint}
        className="px-3 py-2 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300 transition-colors"
        title="Print"
      >
        <Printer className="w-4 h-4" />
        Print
      </button>
    </div>
  );
};

export default ExportButtons;
